import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import axiosInstance from "../../../app/axiosInstance";
import { prepareHeaders } from "../../auth/slice/AuthSlice";
import { handleApiError } from "../../common/utils/ErrorHandler";
import { PER_PAGE_CUSTOMER } from "../../../constants/pagination";

const customerUrl = "api/admin/customer";

const CUSTOMER_VERIFIED = 1;
const CUSTOMER_UNVERIFIED = 0;

const fetchCustomers = async (
  { page = 1, filter = "", perPage = PER_PAGE_CUSTOMER, verified = "" },
  { getState, rejectWithValue, dispatch }
) => {
  try {
    const state = getState();
    const response = await axiosInstance.get(`${customerUrl}/index`, {
      params: { page, per_page: perPage, search: filter, verified },
      headers: prepareHeaders(state),
    });
    return response.data;
  } catch (error) {
    return handleApiError(error, rejectWithValue, dispatch);
  }
};

export const fetchVerifiedCustomers = createAsyncThunk(
  "customers/fetchVerifiedCustomers",
  (arg, { getState, rejectWithValue, dispatch }) =>
    fetchCustomers(
      { verified: CUSTOMER_VERIFIED, ...arg },
      { getState, rejectWithValue, dispatch }
    )
);

export const fetchUnverifiedCustomers = createAsyncThunk(
  "customers/fetchUnverifiedCustomers",
  (arg, { getState, rejectWithValue, dispatch }) =>
    fetchCustomers(
      { verified: CUSTOMER_UNVERIFIED, ...arg },
      { getState, rejectWithValue, dispatch }
    )
);

export const fetchCustomerList = createAsyncThunk(
  "customers/fetchCustomerList",
  async (search, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const response = await axiosInstance.get(`${customerUrl}/list`, {
        params: { search },
        headers: prepareHeaders(state),
      });
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const importCustomerCSVData = createAsyncThunk(
  "customers/importCustomerCSVData",
  async (csvData, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const formData = new FormData();
      formData.append("csv_file", csvData);

      const response = await axiosInstance.post(
        `${customerUrl}/process-csv-file`,
        formData,
        {
          headers: prepareHeaders(state, true, {
            "Content-Type": "multipart/form-data",
          }),
        }
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Async thunk for fetching customer
export const fetchCustomer = createAsyncThunk(
  "customers/fetchCustomer",
  async (id, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const response = await axiosInstance.get(`${customerUrl}/show/${id}`, {
        headers: prepareHeaders(state),
      });
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  }
);

// Async thunk for creating customer
export const createCustomer = createAsyncThunk(
  "customers/createCustomer",
  async (customer, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const response = await axiosInstance.post(
        `${customerUrl}/store`,
        customer,
        {
          headers: prepareHeaders(state),
        }
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Async thunk for updating customer
export const updateCustomer = createAsyncThunk(
  "customers/updateCustomer",
  async (customer, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      let customerData = state.customer.customer;
      const response = await axiosInstance.post(
        `${customerUrl}/update/${customerData.id}`,
        customer,
        {
          headers: prepareHeaders(state),
        }
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Send verification link to unverified customers.
// Payload: { customer_ids: [] } or { select_all: true, search, excluded_ids: [] }
export const sendVerificationLink = createAsyncThunk(
  "customers/sendVerificationLink",
  async (data, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const response = await axiosInstance.post(
        `${customerUrl}/send-verification-link`,
        data,
        {
          headers: prepareHeaders(state),
        }
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

const CustomerSlice = createSlice({
  name: "customer",
  initialState: {
    loading: false,
    isModalLoading: false,
    customer: null,
    customerList: [],
    verifiedLoading: false,
    verifiedCustomers: [],
    verifiedFilter: "",
    verifiedPerPage: PER_PAGE_CUSTOMER,
    verifiedPageCount: 0,
    verifiedCurrentPage: 0,
    verifiedTotal: 0,
    unverifiedLoading: false,
    unverifiedCustomers: [],
    unverifiedFilter: "",
    unverifiedPerPage: PER_PAGE_CUSTOMER,
    unverifiedPageCount: 0,
    unverifiedCurrentPage: 0,
    unverifiedTotal: 0,
    isSendingVerification: false,
    verifiedError: "",
    unverifiedError: "",
    error: "",
    validationError: [],
  },
  reducers: {
    setVerifiedFilter: (state, action) => {
      state.verifiedFilter = action.payload;
      state.verifiedCurrentPage = 0;
    },
    setVerifiedPerPage: (state, action) => {
      state.verifiedPerPage = action.payload;
    },
    setVerifiedPage: (state, action) => {
      state.verifiedCurrentPage = action.payload;
    },
    setUnverifiedFilter: (state, action) => {
      state.unverifiedFilter = action.payload;
      state.unverifiedCurrentPage = 0;
    },
    setUnverifiedPerPage: (state, action) => {
      state.unverifiedPerPage = action.payload;
    },
    setUnverifiedPage: (state, action) => {
      state.unverifiedCurrentPage = action.payload;
    },
    clearError: (state) => {
      state.error = "";
    },
    clearValidationError: (state) => {
      state.error = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch verified customers
      .addCase(fetchVerifiedCustomers.pending, (state) => {
        state.verifiedLoading = true;
        state.verifiedError = "";
      })
      .addCase(fetchVerifiedCustomers.fulfilled, (state, action) => {
        state.verifiedLoading = false;
        const payload = action.payload?.payload;
        state.verifiedCustomers = payload?.data ?? [];
        state.verifiedPageCount = payload?.last_page ?? 0;
        state.verifiedTotal = payload?.total ?? 0;
        state.verifiedCurrentPage = payload?.current_page
          ? payload.current_page - 1
          : 0;
      })
      .addCase(fetchVerifiedCustomers.rejected, (state, action) => {
        state.verifiedLoading = false;
        state.verifiedError = action.payload;
      })
      // fetch unverified customers
      .addCase(fetchUnverifiedCustomers.pending, (state) => {
        state.unverifiedLoading = true;
        state.unverifiedError = "";
      })
      .addCase(fetchUnverifiedCustomers.fulfilled, (state, action) => {
        state.unverifiedLoading = false;
        const payload = action.payload?.payload;
        state.unverifiedCustomers = payload?.data ?? [];
        state.unverifiedPageCount = payload?.last_page ?? 0;
        state.unverifiedTotal = payload?.total ?? 0;
        state.unverifiedCurrentPage = payload?.current_page
          ? payload.current_page - 1
          : 0;
      })
      .addCase(fetchUnverifiedCustomers.rejected, (state, action) => {
        state.unverifiedLoading = false;
        state.unverifiedError = action.payload;
      })
      // send verification link
      .addCase(sendVerificationLink.pending, (state) => {
        state.isSendingVerification = true;
      })
      .addCase(sendVerificationLink.fulfilled, (state) => {
        state.isSendingVerification = false;
      })
      .addCase(sendVerificationLink.rejected, (state) => {
        state.isSendingVerification = false;
      })
      // fetch customer list
      .addCase(fetchCustomerList.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(fetchCustomerList.fulfilled, (state, action) => {
        state.loading = false;
        state.customerList = action.payload.payload;
      })
      .addCase(fetchCustomerList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // import customer csv data
      .addCase(importCustomerCSVData.pending, (state) => {
        state.isModalLoading = true;
        state.error = null;
      })
      .addCase(importCustomerCSVData.fulfilled, (state, action) => {
        state.isModalLoading = false;
      })
      .addCase(importCustomerCSVData.rejected, (state, action) => {
        state.isModalLoading = false;
        if (typeof action.payload === "object") {
          state.validationError = action.payload.errors;
        }
      })
      // Fetch customer
      .addCase(fetchCustomer.pending, (state) => {
        state.isModalLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomer.fulfilled, (state, action) => {
        let data = action.payload.payload;
        state.customer = data;
        state.isModalLoading = false;
      })
      .addCase(fetchCustomer.rejected, (state, action) => {
        state.isModalLoading = false;
        state.error = action.payload;
      })
      // create customer
      .addCase(createCustomer.pending, (state) => {
        state.isModalLoading = true;
        state.error = null;
      })
      .addCase(createCustomer.fulfilled, (state) => {
        // the active tab refetches, since the new customer's verified state decides its list
        state.isModalLoading = false;
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.isModalLoading = false;
        if (typeof action.payload !== "string") {
          const errors = action.payload.errors;
          let validationErrors = {};
          Object.keys(errors)?.forEach((key) => {
            let myKey = key.replace("customers.0.", "").trim();
            validationErrors[myKey] = errors[key];
          });
          state.validationError = validationErrors;
        }
      })
      // update customer
      .addCase(updateCustomer.pending, (state) => {
        state.isModalLoading = true;
        state.error = null;
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        let data = action.payload.payload;
        ["verifiedCustomers", "unverifiedCustomers"].forEach((key) => {
          const index = state[key].findIndex(
            (customer) => customer.id === data?.id
          );
          if (index !== -1) {
            state[key][index] = data;
          }
        });
        state.customer = data;
        state.isModalLoading = false;
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.isModalLoading = false;
        if (typeof action.payload !== "string") {
          state.validationError = action.payload.errors;
        }
      });
  },
});

export const {
  setVerifiedFilter,
  setVerifiedPerPage,
  setVerifiedPage,
  setUnverifiedFilter,
  setUnverifiedPerPage,
  setUnverifiedPage,
  clearError,
  clearValidationError,
} = CustomerSlice.actions;

export default CustomerSlice.reducer;
